import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  ColorPicker,
  Input,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  SettingOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { catalogosApi, type CatalogoTag, type CatalogoTipo } from "../services/catalogos.api";
import { tagColor, randomTagColor } from "../utils/tagColors";

/**
 * Componentes reutilizables del CATÁLOGO de etiquetas (rubros, categorías…).
 * Round 2026-06-30. Reemplazan las listas hardcodeadas del frontend:
 *  - `CatalogManagerModal`: CRUD completo (crear/editar/color/reordenar/borrar).
 *  - `CatalogSelect`: selector (single o multiple) alimentado por el catálogo,
 *    con chips de color y un botón ⚙ para abrir el gestor.
 * Ver docs/modulo-catalogos.md.
 */

// ─── Modal de gestión (CRUD + color + reorden) ───────────────────────────────

export function CatalogManagerModal({
  tipo,
  title,
  open,
  onClose,
  onChanged,
}: {
  tipo: CatalogoTipo;
  title: string;
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const [tags, setTags] = useState<CatalogoTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  // Cada etiqueta nueva arranca con un color AL AZAR (el admin lo puede cambiar).
  const [newColor, setNewColor] = useState<string>(randomTagColor());

  const reload = useCallback(async () => {
    setLoading(true);
    const res = await catalogosApi.list(tipo);
    setTags(res?.data ?? []);
    setLoading(false);
  }, [tipo]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const notify = () => onChanged?.();

  const add = async () => {
    const nombre = newName.trim();
    if (!nombre) return;
    const res = await catalogosApi.create({ tipo, nombre, color: newColor });
    if (res?.success) {
      setNewName("");
      setNewColor(randomTagColor()); // próxima etiqueta, otro color al azar
      await reload();
      notify();
      message.success("Etiqueta creada");
    }
  };

  const rename = async (t: CatalogoTag, nombre: string) => {
    const n = nombre.trim();
    if (!n || n === t.nombre) return;
    const res = await catalogosApi.update(t.id, { nombre: n });
    if (res?.success) {
      await reload();
      notify();
    }
  };

  const recolor = async (t: CatalogoTag, color: string | null) => {
    const res = await catalogosApi.update(t.id, { color });
    if (res?.success) {
      await reload();
      notify();
    }
  };

  const remove = async (t: CatalogoTag) => {
    const res = await catalogosApi.remove(t.id);
    if (res?.success) {
      await reload();
      notify();
    }
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= tags.length) return;
    const next = [...tags];
    [next[idx], next[j]] = [next[j], next[idx]];
    setTags(next);
    await catalogosApi.reorder(
      tipo,
      next.map((t) => t.id),
    );
    notify();
  };

  return (
    <Modal open={open} onCancel={onClose} title={title} footer={null} width={540}>
      <List
        size="small"
        loading={loading}
        dataSource={tags}
        locale={{ emptyText: "Sin etiquetas todavía. Crea la primera abajo." }}
        renderItem={(t, idx) => (
          <List.Item
            actions={[
              <Button
                key="up"
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={idx === 0}
                onClick={() => move(idx, -1)}
              />,
              <Button
                key="down"
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={idx === tags.length - 1}
                onClick={() => move(idx, 1)}
              />,
              <Popconfirm
                key="del"
                title="¿Eliminar esta etiqueta?"
                description="Los registros que la usaban conservan el texto."
                okText="Eliminar"
                cancelText="Cancelar"
                onConfirm={() => remove(t)}
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>,
            ]}
          >
            <Space>
              <ColorPicker
                size="small"
                value={tagColor(t.nombre, t.color)}
                onChangeComplete={(c) => recolor(t, c.toHexString())}
              />
              <Tag color={tagColor(t.nombre, t.color)} style={{ margin: 0 }}>
                <Typography.Text
                  style={{ color: "#fff" }}
                  editable={{ onChange: (v) => rename(t, v), tooltip: "Renombrar" }}
                >
                  {t.nombre}
                </Typography.Text>
              </Tag>
            </Space>
          </List.Item>
        )}
      />
      <Space.Compact style={{ display: "flex", marginTop: 12 }}>
        <ColorPicker value={newColor} onChangeComplete={(c) => setNewColor(c.toHexString())} />
        <Input
          placeholder="Nueva etiqueta…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={add}
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={add}>
          Agregar
        </Button>
      </Space.Compact>
    </Modal>
  );
}

// ─── Selector alimentado por el catálogo (single / multiple) ──────────────────

export function CatalogSelect({
  tipo,
  mode,
  value,
  onChange,
  placeholder,
  manageTitle,
  style,
  size,
  onCatalogChanged,
}: {
  tipo: CatalogoTipo;
  mode?: "multiple";
  value: string | string[] | null | undefined;
  onChange: (v: any) => void;
  placeholder?: string;
  manageTitle: string;
  style?: React.CSSProperties;
  size?: "small" | "middle" | "large";
  /** Se llama cuando el catálogo cambia (crear/editar/borrar/reordenar), para
   *  que el padre refresque sus propias listas (chips de tabla, filtros, etc.). */
  onCatalogChanged?: () => void;
}) {
  const [tags, setTags] = useState<CatalogoTag[]>([]);
  const [managerOpen, setManagerOpen] = useState(false);

  const reload = useCallback(async () => {
    const res = await catalogosApi.list(tipo);
    setTags(res?.data ?? []);
  }, [tipo]);

  const onManagerChanged = useCallback(() => {
    reload();
    onCatalogChanged?.();
  }, [reload, onCatalogChanged]);

  useEffect(() => {
    reload();
  }, [reload]);

  const colorByName = useMemo(() => {
    const m = new Map<string, string | null | undefined>();
    for (const t of tags) m.set(t.nombre, t.color);
    return m;
  }, [tags]);

  const options = useMemo(() => {
    const names = new Set(tags.map((t) => t.nombre));
    const current = Array.isArray(value) ? value : value ? [value] : [];
    // Valores actuales que no estén en el catálogo (ej. escritos antes) se agregan
    // como opción ad-hoc para que se muestren igual.
    const extra = current
      .filter((v) => v && !names.has(v))
      .map((v) => ({ value: v, label: v }));
    return [...tags.map((t) => ({ value: t.nombre, label: t.nombre })), ...extra];
  }, [tags, value]);

  return (
    <Space.Compact style={{ display: "flex", width: "100%", ...style }}>
      <Select
        mode={mode}
        size={size}
        style={{ flex: 1 }}
        placeholder={placeholder}
        value={value ?? undefined}
        onChange={onChange}
        options={options}
        showSearch
        optionFilterProp="value"
        allowClear
        tagRender={
          mode === "multiple"
            ? (props) => (
                <Tag
                  color={tagColor(String(props.value), colorByName.get(String(props.value)))}
                  closable={props.closable}
                  onClose={props.onClose}
                  style={{ marginInlineEnd: 3 }}
                >
                  {props.label}
                </Tag>
              )
            : undefined
        }
      />
      <Button
        icon={<SettingOutlined />}
        size={size}
        title="Gestionar etiquetas"
        onClick={() => setManagerOpen(true)}
      />
      <CatalogManagerModal
        tipo={tipo}
        title={manageTitle}
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        onChanged={onManagerChanged}
      />
    </Space.Compact>
  );
}
